import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter } from 'rxjs';

import { Theme } from '../../helper/theme';
import { AppSettingsUrl } from '../appSettings';
import { AuthService } from './auth.service';
import { BodyService } from './body.service';
import { InvalidUser, UserRoles, apiBase } from './external/api';
import { isSet } from './external/storage';
import { adminApi } from './external/api/admin';
import { userApi } from './external/api/user';
import { TimezoneInfo } from './external/charting_library';
import { AutoScaleReset, GeneralConfig } from './external/config/general-config';
import { Bias, DrawingControl, InitHubOptions, UpdateViewField, UpdateViewType, UserNotification, UserNotificationType, hub } from './external/hub';
import { TVContext, TargetsPreview, tvbar } from './external/hub/context';
import { TVHelper } from './external/hub/helper';
import { InfoBuilder } from './external/smpa/infobox';
import { TranslationService } from './translation.service';
import { BarData } from './external/hub/datafeed';
import { AtrsInd, getCustomInds } from './external/smpa/inds';
import { BuiltAlertTicker, UserSettings } from './external/config/user';
import { AlertsService } from './alerts.service';
import { SettingsScope, ChartConfig } from './external/config/config';
import { UserTheme, LanguageType } from './external/config/misc';
import { TickerInfo, TimeFrame } from './external/config/tickers';

@Injectable()
export class HubService {
    private userNotificationEvent: Subject<UserNotification> = new Subject<UserNotification>();
    //private clipDataChangedEvent: Subject<TVContext> = new Subject<TVContext>();
    private barReplayChangedEvent: Subject<TVContext> = new Subject<TVContext>();
    private crossHairChangedEvent: Subject<CrossHairChangedArgs> = new Subject<CrossHairChangedArgs>();
    private chartChangedEvent: Subject<ChartChangedArgs> = new Subject<ChartChangedArgs>();
    private changeSettingsEvent: BehaviorSubject<SettingsScope> = new BehaviorSubject<SettingsScope>(null);
    private chartsInitedEvent: Subject<void> = new Subject<void>();
    private settingsEvent: BehaviorSubject<UserSettings> = new BehaviorSubject<UserSettings>(null);
    private tickersChangedEvent: Subject<void> = new Subject<void>();
    private tickersCacheChangedEvent: Subject<TickerInfo[]> = new Subject<TickerInfo[]>();
    private hubBusyEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private autoScaleEvent: Subject<AutoScaleChangedArgs> = new Subject<AutoScaleChangedArgs>();
    private alertMsgsChangedEvent: Subject<BuiltAlertTicker> = new Subject<BuiltAlertTicker>();
    private reconnectedEvent: Subject<void> = new Subject<void>();
    private screenshotReadyEvent: Subject<string> = new Subject<string>();

    getUserNotification = (): Observable<UserNotification> => this.userNotificationEvent.asObservable();
    //getClipDataChanged = (): Observable<TVContext> => this.clipDataChangedEvent.asObservable();
    getBarReplayChanged = (): Observable<TVContext> => this.barReplayChangedEvent.asObservable();
    getCrossHairChanged = (): Observable<CrossHairChangedArgs> => this.crossHairChangedEvent.asObservable();
    getChartChanged = (): Observable<ChartChangedArgs> => this.chartChangedEvent.asObservable();
    getSettingsChanged = (): Observable<SettingsScope> => this.changeSettingsEvent.asObservable().pipe(filter(s => s != null));
    getChartsInited = (): Observable<void> => this.chartsInitedEvent.asObservable();
    getUserSettingsChanged = (): Observable<UserSettings> => this.settingsEvent.asObservable().pipe(filter(s => s != null));
    getTickersChanged = (): Observable<void> => this.tickersChangedEvent.asObservable();
    getTickersCacheChanged = (): Observable<TickerInfo[]> => this.tickersCacheChangedEvent.asObservable();
    getHubBusyChange = (): Observable<boolean> => this.hubBusyEvent.asObservable();
    getAutoScaleChange = (): Observable<AutoScaleChangedArgs> => this.autoScaleEvent.asObservable();
    getScreenshotReadyChange = (): Observable<string> => this.screenshotReadyEvent.asObservable();
    getAlertMsgsChanged = (): Observable<BuiltAlertTicker> => this.alertMsgsChangedEvent.asObservable();
    getReconnectedChanged = (): Observable<void> => this.reconnectedEvent.asObservable();

    private firstInit: boolean = false;

    constructor(private authSrv: AuthService,
        private bodySrv: BodyService,
        private trans: TranslationService,
        private alerts: AlertsService) {

        this.bodySrv.getThemeChanged().subscribe(theme => {
            hub.setTheme(theme == Theme.light ? UserTheme.Light : UserTheme.Dark);
            this.settingsChange();
        });
        TVHelper.onChanged = () => {
            TVHelper.so["paneProperties.legendProperties.showSeriesOHLC"] = false;
        };
    }

    layoutDestroy = () => {
        this.settingsEvent.next(null);
    }

    Init = (isAdminLayout: boolean = false) => {
        const user = this.authSrv.getUserData();
        if (isAdminLayout) {
            apiBase.setAuth(AppSettingsUrl.apiUrl, user.authOut.sid, user.authOut.uid, () => {
                this.authSrv.logout();
            });
            return;
        }

        hub.init({
            apiUrl: AppSettingsUrl.apiUrl,
            sessionId: user.authOut.sid,
            userId: user.authOut.uid,
            smpa: true,
            theme: this.bodySrv.getThemState() == Theme.light ? UserTheme.Light : UserTheme.Dark,
            onSessionExpired: () => this.authSrv.logout(),
            onBusy: (state) => this.hubBusyEvent.next(state),
            legendVisible: true,
            drawingControl: DrawingControl.Lock,
            viewPortSync: false,
            tvstorage: false,
            lang: LanguageType[this.trans.lang],
            isAdmin: isSet(this.authSrv.getUserData().authOut.tls, UserRoles.Admin)
        } as InitHubOptions);
        hub.onScreenshotReady = (url: string) => {
            this.screenshotReadyEvent.next(url);
        };
        hub.onUserNotification = (x) => {
            this.userNotificationEvent.next(x);
        };
        hub.onUserExpired = () => {
            this.userNotificationEvent.next({ type: UserNotificationType.Paused, msg: null });
        };
        hub.onTickersChanged = () => {
            this.tickersChangedEvent.next();
        };
        hub.onTickersCacheChanged = (value: TickerInfo[]) => {
            this.tickersCacheChangedEvent.next(value);
        };
        hub.onAlertMsgsChanged = (msg) => {
            this.alertMsgsChangedEvent.next(msg);
            if (hub.config.general.alerts.playSound)
                if (msg.monitored && msg.expired == null)
                    this.alerts.beep();
            
        };
        hub.onReconnected = () => {
            this.reconnectedEvent.next();
        };
    }

    getTVs = (): TVContext[] =>
        hub.TVs;

    getTVsCount = (): number =>
        hub.count;

    getCrossHairBar = (tileId: number): BarData => {
        if (!hub)
            return null;
        return hub.TVs[tileId]?.getCrossHairBar();
    }
    getTickerInfo = (tileId: number): TickerInfo => {
        if (!hub)
            return null;
        return hub.TVs[tileId]?.getTickerInfo();
    }
    signOut = (): Promise<void> => userApi.signOut().then(() => this.authSrv.logout());

    setBias = (bias: Bias) => {
        hub.setBias(bias);
    }

    getBias = (): Bias => {
        return hub.bias;
    }

    setTileBias = (id: number, bias: Bias) => {
        const tile = hub.TVs[id];
        if (!tile)
            return;
        tile.setBias(bias);
    }

    getTileBias = (id: number): Bias => {
        const tile = hub.TVs[id];
        if (!tile)
            return Bias.Both;
        return tile.bias;
    }

    makeScreenshot = (id: number): string => {
        let r: string = null;
        const tile = hub.TVs[id];
        if (tile && tile.isTvReady())
            tile.takeScreenshot();

        return r;
    }

    targetsPreview = (id: number) => {
        const tile = hub.TVs[id];
        if (!tile)
            return TargetsPreview.No;
        return tile.targetsPreview;
    }

    setTargetsPreview = (id: number, type: TargetsPreview) => {
        const tile = hub.TVs[id];
        if (tile)
            tile.setTargetsPreview(type);
    }

    drawingVisible = (id: number): boolean => {
        let r = !hub.config || hub.config.general.viewPort.autoScaleReset == AutoScaleReset.Never;
        if (id == null) {
            for (let i = 0; i < hub.count; i++)
                if (hub.TVs[i].drawingVisible) {
                    r = true;
                    break;
                }

        } else {
            const tile = hub.TVs[id];
            if (tile)
                r = tile.drawingVisible;
        }
        return r;
    }

    getToolBarWidth = (id: number): number => {
        const tile = hub.TVs[id];
        return tile.getDrawingToolbarWidth();
    }

    setAutoScale = (id: number, autoScale: boolean) => hub.TVs[id]?.setAutoScale(false, autoScale);

    settingsChange = () => this.changeSettingsEvent.next(hub.config);

    createTv = (elementId: string, tickerId: string, timeFrame: string, infoBuilder: InfoBuilder): TVContext => {

        const isdev = isDevMode();

        let tvd = hub.createTV(elementId, '../assets/charting_library/', tickerId, timeFrame, infoBuilder, (options) => {
            //if (isdev)
            //	options.custom_indicators_getter = getCustomInds;
        }, false);

        //if (isdev)
        //	tvd.tv.onChartReady(() => {
        //		tvd.tv.activeChart().createStudy(AtrsInd.Id);
        //	});

        tvd.onCrossHairChanged = (x, bar) => {
            this.crossHairChangedEvent.next({ tvd: x, bar: bar });
        };
        tvd.onTickerChanged = (x, t) => {
            this.chartChangedEvent.next({ tvd: x });
        };
        tvd.onAutoScaleChanged = (x, tv) => {
            this.autoScaleEvent.next({ tvd: x, tv: tv });
        };
        tvd.onBarReplayChanged = (x) => {
            this.barReplayChangedEvent.next(x);
        };
        //tvd.onClipDataChanged = (x) => {
        //	this.clipDataChangedEvent.next(x);
        //};
        return tvd;
    }

    setTicker = (tickerId: string, timeFrame: string, tvId: number) => {
        if (!hub.isUpdating())
            console.log("setTicker");

        if (this.firstInit)
            hub.TVs[tvId].setTicker(tickerId, timeFrame);
    }

    beginUpdate = () => hub.beginUpdate();

    endUpdate = () => {
        hub.endUpdate();
        this.firstInit = true;
        this.chartsInitedEvent.next();
    }

    beginSetTicker = () => hub.beginSetTicker();
    endSetTicker = () => hub.endSetTicker();

    clean = () => {
        let count = hub.count;
        this.beginUpdate();
        this.endUpdate();
        count = Math.min(count, hub.TVs.length);
        for (let i = 0; i < count; i++) {
            let tv = hub.TVs[i];
            let iFrame = tv.htmlHost.firstChild;
            tv.htmlHost.removeChild(iFrame);
            tv.htmlHost = iFrame as HTMLElement;
        }
    }

    getConfig = (): SettingsScope => hub.config;

    setConfig = (value: SettingsScope): void => {
        hub.config.general = value.general;
        hub.config.chart = value.chart;
        setTimeout(() => this.updateView(), 0);
    }

    setGeneralConfig = (value: GeneralConfig): void => {
        hub.config.general = value;
        this.updateView(UpdateViewType.General);
    }
    setChartConfig = (value: ChartConfig): void => {
        hub.config.chart = value;
        this.updateView(UpdateViewType.TVSmpa);
    }

    getTimzones = (): readonly TimezoneInfo[] => hub.getTimeZones();

    getTvTheme = (): UserTheme => hub.tvtheme;
    getUITheme = (): UserTheme => hub.uitheme;

    updateView = (type = UpdateViewType.All, field = UpdateViewField.Some) => {
        hub.updateView(type, field);
        this.settingsChange();
    }

    getDataBar = (id: number, ts: number) => hub.TVs[id].getDataBarIdx(ts);

    pingSMPA = () => adminApi.pingSMPA(null, hub.first);

    clearCachedBars = () => {

        adminApi.clearCachedBars(hub.first).then(() => {
            for (const tv of hub.TVs)
                tv.resetCache();
        });

    }

    getTimeFrame = (tileId: number): string =>
        hub.TVs[tileId].tf;

    getTimeFrameInfo = (tileId: number): TimeFrame =>
        hub.TVs[tileId].getTimeFrameInfo();

    getTicker = (tileId: number): string =>
        hub.TVs[tileId].tid;

    getUserSettings = (): Promise<UserSettings> => new Promise<UserSettings>((res, rej) => {
        this.layoutDestroy();
        hub.getUserSettings().catch(d => {
            rej(d);
            return null as UserSettings;
        }).then(data => {
            if (data == null)
                return;
            if (data.ui.lang != this.trans.lang)
                userApi.setLang(this.trans.lang);

            this.authSrv.InitSettings(data);
            res(data);

            this.settingsEvent.next(data);
        });
    });
}

export interface CrossHairChangedArgs {
    tvd: TVContext;
    bar: BarData;
}

export interface ChartChangedArgs {
    tvd: TVContext
}

export interface AutoScaleChangedArgs extends ChartChangedArgs {
    tv: boolean
}
